import { HygieneScoreValueMapping, OralScoreValueMapping, RiskValuekMapping } from 'consts/oralScoreConsts'
import { calculateOralScore, getPresentedScoreCategory } from 'logic/oralScoresLogic'
import { useMemo } from 'react'

/**
 * Calculates the aggregated Oral Score based on the oralScores dictionary returned from the scan summary model and converts the numeric values to `OralScores` enum.
 *
 * Prefers backend presentation categories for all three scores, with model-category fallback.
 *
 * @param {Object} args
 * @param {Object} args.oralScores The oralScores dictionary from the TimelineItem payload: the model fields (`hygiene_score`, `oral_hygiene_score`, `dental_risk`, `perio_risk`, `periodontal_risk`) plus the backend presented fields (`hygiene_presented_category` etc). See `oralScoreConsts.js`.
 * @returns An object with the oral score values mapped to OralScore enum. If the original oral score object is not valid, the returned object would be empty.
 */
export default ({ oralScores }) => {
  const hasOralScores = useMemo(() => oralScores && !oralScores?.errorCode, [oralScores])

  const oralScoreNumeric = useMemo(() => {
    if (!oralScores || oralScores.errorCode) {
      return null
    }

    const hygiene = getPresentedScoreCategory(oralScores, 'oral_hygiene_score')
    const dental = getPresentedScoreCategory(oralScores, 'dental_risk')
    const perio = getPresentedScoreCategory(oralScores, 'periodontal_risk')
    if ([hygiene, dental, perio].some(score => score == null)) return null

    return calculateOralScore({ hygiene_score: hygiene, dental_risk: dental, perio_risk: perio })
  }, [oralScores])

  return hasOralScores
    ? {
        oralScoreNumeric,
        oralScore: OralScoreValueMapping[oralScoreNumeric],
        hygieneScore: HygieneScoreValueMapping[getPresentedScoreCategory(oralScores, 'oral_hygiene_score')],
        perioRisk: RiskValuekMapping[getPresentedScoreCategory(oralScores, 'periodontal_risk')],
        dentalRisk: RiskValuekMapping[getPresentedScoreCategory(oralScores, 'dental_risk')]
      }
    : {}
}
